import { getNameToSave } from '@griply/utils';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  ResizableTextArea,
  Variants,
} from 'shared/components/ui/resizable-text-area';
import { useDelayedCallback } from 'shared/hooks/use-delayed-callback';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';

import { useTaskDetailShortcut } from '../../hooks/use-task-detail-shortcut';

export type TaskTitleProps = {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  completed?: boolean;
  isFrozen?: boolean;
  instantUpdate?: boolean;
};

export const Title = forwardRef<HTMLTextAreaElement, TaskTitleProps>(
  (
    {
      value,
      placeholder,
      onChange,
      onSubmit,
      onFocus,
      onBlur,
      completed,
      isFrozen,
      instantUpdate,
    },
    ref,
  ) => {
    const inputRef = useForwardedRef(ref);
    const focussedRef = useRef(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [delayedOnChange, killOnChangeDelay] = useDelayedCallback(
      onChange,
      500,
    );

    const _onChange = (newValue: string) => {
      setCurrentValue(newValue);

      if (instantUpdate) {
        onChange(newValue);
        return;
      }

      delayedOnChange(newValue);
    };

    const _onBlur = () => {
      focussedRef.current = false;
      onBlur?.();
      killOnChangeDelay();
      const nameToSave = getNameToSave(currentValue, value);
      if (!nameToSave) {
        setCurrentValue(value);
        return;
      }

      onChange(nameToSave);
    };

    const _onFocus = () => {
      focussedRef.current = true;
      onFocus?.();
    };

    useEffect(() => {
      if (!focussedRef.current) {
        setCurrentValue(value ?? '');
      }
    }, [value]);

    useTaskDetailShortcut(
      ['Enter'],
      (e) => {
        if (document.activeElement === inputRef.current) {
          e.preventDefault();
          if (onSubmit) {
            onSubmit(currentValue);
            killOnChangeDelay();
            return;
          }

          inputRef.current?.blur();
        }
      },
      { otherKeysInCombinationAllowed: true },
    );

    return (
      <ResizableTextArea
        ref={inputRef}
        value={currentValue}
        placeholder={placeholder}
        onChange={_onChange}
        onFocus={_onFocus}
        onBlur={_onBlur}
        variant={Variants.Subtitle1}
        isStrikeThrough={completed}
        isFrozen={isFrozen}
        fullWidth
      />
    );
  },
);
